/**
 * Escopo fechado: só o ramo “Ver orçamento” quando a agência permite fluxo simplificado —
 * decidir entre modal de criação vs orçamento já existente (simplificado vs tradicional),
 * lendo `activeBudget` + item em `budget` com tolerância a Immutable.
 */

import { getBudget } from '_modules/budget/actions'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_utils/constants/permissions'
import { navigateToExpiredLink } from '_utils/expired-link-navigation'

export function getActiveBudgetIdFromServiceOrder(serviceOrder) {
  if (!serviceOrder) return null
  return serviceOrder.get?.('activeBudget') ?? serviceOrder.activeBudget ?? null
}

export function findBudgetEntryInServiceOrder(serviceOrder, activeBudgetId) {
  if (serviceOrder == null || activeBudgetId == null) return null
  const list = serviceOrder.get?.('budget') ?? serviceOrder.budget
  if (!list || (list.size !== undefined && list.size === 0)) return null
  const aid = Number(activeBudgetId)
  const findFn = b => {
    if (!b) return false
    const id = b.get?.('id') ?? b.id
    return Number(id) === aid
  }
  if (typeof list.find === 'function') {
    return list.find(findFn) ?? null
  }
  return null
}

export function getIsSimplifiedFromBudgetEntry(entry) {
  if (!entry) return undefined
  const raw =
    entry.get?.('isSimplified') ??
    entry.get?.('is_simplified') ??
    entry.isSimplified ??
    entry.is_simplified
  if (typeof raw === 'boolean') return raw
  if (raw === 'true') return true
  if (raw === 'false') return false
  return undefined
}

/**
 * Ramo compartilhado: detalhes do prestador (`service-order-details`) vs header SAAS (`service-order/header`).
 * Passe URLs em `variants` (ex.: `?tipo=externo` só no header) e falhas rígidas em `onGetBudgetHardFailure`.
 */
export function createHandleBudgetPageSelection({
  serviceOrder,
  serviceOrderId,
  user,
  dispatch,
  navigate,
  isNewBudgetFeatureActive,
  checkUserPermission,
  showToast,
  formatErrorMessage,
  onOpenChoiceModal,
  onTraditionalFlow,
  onSimplifiedFlow,
  beforeFetchBudget,
  afterFetchBudget,
  onGetBudgetHardFailure,
  variants,
}) {
  return function handleBudgetPageSelection() {
    const agency = serviceOrder?.agency?.toJS?.() ?? serviceOrder?.agency
    if (!agency?.allowsSimplifiedBudget) {
      return onTraditionalFlow()
    }

    const activeBudgetId = getActiveBudgetIdFromServiceOrder(serviceOrder)
    if (!activeBudgetId) {
      return onOpenChoiceModal()
    }

    const entry = findBudgetEntryInServiceOrder(serviceOrder, activeBudgetId)
    const isSimplifiedFlag = getIsSimplifiedFromBudgetEntry(entry)

    if (isSimplifiedFlag === true) {
      return onSimplifiedFlow()
    }
    if (isSimplifiedFlag === false) {
      return onTraditionalFlow()
    }

    checkUserPermission({
      permission: PERMISSIONS.ORIGINAL_BUDGETS,
      permissionAction: PERMISSIONS_ACTIONS.READ,
      functionAccess: () => {
        const serviceOrderTradesmanId = serviceOrder?.get?.('tradesman') ?? serviceOrder?.tradesman
        if (
          user?.getServiceProviderId !== serviceOrderTradesmanId &&
          !serviceOrder?.get?.('tradesmanInPoolCandidates') &&
          !serviceOrder?.tradesmanInPoolCandidates
        ) {
          return navigateToExpiredLink()
        }

        beforeFetchBudget()
        return dispatch(getBudget(serviceOrderId, activeBudgetId))
          .then(data => {
            afterFetchBudget()
            const simpl = getIsSimplifiedFromBudgetEntry(data)
            const id = data?.id ?? activeBudgetId
            if (simpl === true) {
              navigate(variants.buildSimplified(id))
            } else if (isNewBudgetFeatureActive) {
              navigate(variants.buildTraditionalNew(id))
            } else {
              navigate(variants.buildTraditionalLegacy(id))
            }
          })
          .catch(error => {
            if (error?.redirect === false) {
              showToast({ type: 'error', message: formatErrorMessage(error?.message) })
            } else {
              onGetBudgetHardFailure()
            }
            afterFetchBudget()
          })
      },
    })
    return null
  }
}
